import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Loader } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter } from 'components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { Checkbox } from 'components/ui/checkbox';
import { Button } from 'components/ui/ButtonGs';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/ui/confirmation-dialog';
import { notify } from 'services/notifications';
import { useViagensBilling } from 'hooks/api/viagens-billing';
import type { ViagemBilling } from '../types';
import { Formatter } from 'utils';
import { formatViagemCurrency, parseViagemCurrencyValue } from '../utils';
import { ViagensBillingRejectDialog } from './viagens-billing-reject-dialog';
import { ViagensBillingEstornoDialog } from './viagens-billing-estorno-dialog';

type Props = {
  billing: ViagemBilling | null;
  canRemoveEstadiaFromBilling: boolean;
  canReverseEstadiaBilling: boolean;
  onOpenChange: (open: boolean) => void;
};

const EMPTY_BILLING_ITEMS: NonNullable<ViagemBilling['billingItems']> = [];

const formatDate = (value?: string | null) => {
  return value
    ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm:ss' })
    : '---';
};

const parseDurationToMinutes = (value?: string | null) => {
  if (!value) return 0;

  const [hours = 0, minutes = 0] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const formatDuration = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const clearBodyPointerLock = () => {
  [0, 50, 150, 300].forEach((delay) => {
    window.setTimeout(() => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }, delay);
  });
};

export const ViagensBillingDetailDialog = ({
  billing,
  canRemoveEstadiaFromBilling,
  canReverseEstadiaBilling,
  onOpenChange,
}: Props) => {
  const queryClient = useQueryClient();
  const {
    fetchBillingById,
    removeViagensFromBilling,
    approveBilling,
    rejectBilling,
    reverseBilling,
  } = useViagensBilling();
  const removeConfirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);
  const approveConfirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);
  const [selected, setSelected] = React.useState<number[]>([]);
  const [rejectingBillingId, setRejectingBillingId] = React.useState<number | null>(null);
  const [reversingBillingId, setReversingBillingId] = React.useState<number | null>(null);
  const open = Boolean(billing);

  const billingQuery = useQuery({
    queryKey: ['viagens-billing-detail', billing?.id],
    queryFn: () => fetchBillingById(billing?.id as number),
    enabled: Boolean(billing?.id),
    refetchOnWindowFocus: false,
  });

  const data = billingQuery.data;
  const billingItems = data?.billingItems ?? EMPTY_BILLING_ITEMS;
  const isReversed = Boolean(data?.reversedAt) || Boolean(data?.activities?.some((activity) => activity.operation === 'REVERSE'));
  const isApproved = Boolean(data?.approvedAt);
  const isPending = Boolean(data?.id) && !data?.approvedAt && !data?.rejectedAt && !isReversed;
  const canManageBillingItems = canRemoveEstadiaFromBilling && !isApproved;
  const totalValue = React.useMemo(() => {
    if (data?.totalValue !== null && data?.totalValue !== undefined) {
      return data.totalValue;
    }

    return billingItems.reduce(
      (total, item) =>
        total + parseViagemCurrencyValue(item.viagem?.valorEstadia),
      0
    );
  }, [billingItems, data?.totalValue]);
  const totalHours = React.useMemo(
    () =>
      formatDuration(
        billingItems.reduce(
          (total, item) =>
            total + parseDurationToMinutes(item.viagem?.estadiaPagar),
          0
        )
      ),
    [billingItems]
  );

  const allSelected = React.useMemo(() => {
    return Boolean(
      billingItems.length &&
      billingItems.every((item) => selected.includes(item.viagemControleId))
    );
  }, [billingItems, selected]);

  const toggleAll = React.useCallback(() => {
    setSelected((state) => {
      if (state.length) return [];
      return billingItems.map((item) => item.viagemControleId);
    });
  }, [billingItems]);

  const toggleItem = React.useCallback((id: number, checked: boolean) => {
    setSelected((state) => {
      if (checked) return [...new Set([...state, id])];
      return state.filter((itemId) => itemId !== id);
    });
  }, []);

  const closeDialog = React.useCallback(() => {
    setSelected([]);
    onOpenChange(false);
    clearBodyPointerLock();
  }, [onOpenChange]);

  const removeMutation = useMutation({
    mutationKey: ['remove-viagens-from-billing'],
    mutationFn: removeViagensFromBilling,
    onSuccess: () => {
      notify('success', 'Viagens removidas da fatura com sucesso.');
      queryClient.invalidateQueries('fetch-paginated-viagens-billings');
      queryClient.invalidateQueries('fetch-billable-viagens');
      queryClient.invalidateQueries(['viagens-billing-detail', billing?.id]);

      if (allSelected) {
        closeDialog();
      } else {
        setSelected([]);
      }
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const approveMutation = useMutation({
    mutationKey: ['approve-viagem-billing'],
    mutationFn: approveBilling,
    onSuccess: () => {
      notify('success', 'Fatura aprovada com sucesso.');
      queryClient.invalidateQueries('fetch-paginated-viagens-billings');
      queryClient.invalidateQueries(['viagens-billing-detail', billing?.id]);
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const rejectMutation = useMutation({
    mutationKey: ['reject-viagem-billing'],
    mutationFn: rejectBilling,
    onSuccess: () => {
      notify('success', 'Fatura reprovada com sucesso.');
      setRejectingBillingId(null);
      queryClient.invalidateQueries('fetch-paginated-viagens-billings');
      queryClient.invalidateQueries(['viagens-billing-detail', billing?.id]);
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const reverseMutation = useMutation({
    mutationKey: ['reverse-viagem-billing'],
    mutationFn: reverseBilling,
    onSuccess: () => {
      notify('success', 'Fatura estornada com sucesso. As viagens retornaram para validação operacional.');
      setReversingBillingId(null);
      queryClient.invalidateQueries('fetch-paginated-viagens-billings');
      queryClient.invalidateQueries('fetch-billable-viagens');
      queryClient.invalidateQueries('fetch-viagens-controle');
      queryClient.invalidateQueries(['viagens-billing-detail', billing?.id]);
      onOpenChange(false);
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const handleApprove = React.useCallback(async () => {
    if (!data?.id || approveMutation.isLoading) return;
    const confirmation = await approveConfirmationDialogRef.current?.openDialog({
      title: 'Aprovar fatura',
      message: `Tem certeza que deseja aprovar a fatura ${data.id}?`,
    });
    if (confirmation === 'confirmed') approveMutation.mutate(data.id);
  }, [approveMutation, data?.id]);

  const handleReject = React.useCallback(() => {
    if (data?.id) setRejectingBillingId(data.id);
  }, [data?.id]);

  const handleReverse = React.useCallback(() => {
    if (isPending && data?.id) setReversingBillingId(data.id);
  }, [data?.id, isPending]);

  const handleRemove = React.useCallback(async () => {
    if (!selected.length) {
      notify('error', 'Selecione ao menos uma viagem para remover.');
      return;
    }

    const confirmation = await removeConfirmationDialogRef.current?.openDialog({
      title: allSelected
        ? 'Remover todas as viagens'
        : 'Remover viagens selecionadas',
      message: allSelected
        ? 'Tem certeza que deseja remover todas as viagens desta fatura?'
        : 'Tem certeza que deseja remover as viagens selecionadas?',
    });

    if (confirmation === 'confirmed') {
      removeMutation.mutate(selected);
    }
  }, [allSelected, removeMutation, selected]);

  React.useEffect(() => {
    if (!open) {
      setSelected([]);
      clearBodyPointerLock();
    }
  }, [open]);

  React.useEffect(() => {
    if (!canManageBillingItems) {
      setSelected([]);
    }
  }, [canManageBillingItems]);

  React.useEffect(() => {
    document.body.classList.toggle('printing-billing-detail', open);

    return () => {
      document.body.classList.remove('printing-billing-detail');
    };
  }, [open]);
  
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => !nextOpen && closeDialog()}
      >
        <DialogContent className="billing-detail-print w-[96vw] max-w-[1400px] max-h-[92vh] overflow-x-hidden overflow-y-auto bg-white text-slate-950 print:static print:w-full print:max-w-full print:max-h-none print:translate-x-0 print:translate-y-0 print:overflow-visible print:border-none print:p-0 print:shadow-none">
          {billingQuery.isLoading ? (
            <div className="flex items-center justify-center w-full py-20">
              <Loader className="w-10 h-10 animate-spin text-gray-500" />
            </div>
          ) : (
            <>
              <section className="w-full py-8 flex flex-col gap-4">
                <header className="flex justify-between text-xs pb-4 border-slate-950 border-b text-slate-700">
                  <span>Webcol 4PL Corteva</span>
                  <span>
                    Documento impresso em {new Date().toLocaleString('pt-BR')}
                  </span>
                </header>

                <section className="pb-4 border-slate-950 border-b">
                  <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                    Corteva
                  </h6>
                  <p className="text-xs">Controle de viagens e faturas</p>
                </section>

                <section className="pb-4 border-slate-950 border-b">
                  <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                    {data?.carrier?.tradeName || 'Transportadora não informada'}
                  </h6>
                  <p className="text-xs">
                    {data?.carrier?.document
                      ? `Documento: ${data.carrier.document}`
                      : 'Documento não informado'}
                  </p>
                </section>

                <section className="grid grid-cols-5 gap-8 pb-4 border-slate-950 border-b">
                  <div>
                    <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                      Fatura
                    </h6>
                    <p className="text-xs">
                      {`${data?.id ?? ''}`.padStart(3, '0')}
                    </p>
                  </div>
                  <div>
                    <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                      Emissão
                    </h6>
                    <p className="text-xs">{formatDate(data?.createdAt)}</p>
                  </div>
                  <div>
                    <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                      Emitido por
                    </h6>
                    <p className="text-xs">{data?.user?.name || '---'}</p>
                  </div>
                  <div>
                    <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                      Horas
                    </h6>
                    <p className="text-xs">{totalHours}</p>
                  </div>
                  <div>
                    <h6 className="font-sans font-bold text-sm mb-1 uppercase">
                      Valor total
                    </h6>
                    <p className="text-xs">{formatViagemCurrency(totalValue)}</p>
                  </div>
                </section>

                <div className="print:max-h-max screen:max-h-[30vh] screen:overflow-auto">
                  <Table className="[&_th]:px-2 [&_td]:px-2">
                    <TableHeader>
                      <TableRow>
                        {canManageBillingItems && (
                          <TableHead className="w-[20px] text-slate-950 print:hidden">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={toggleAll}
                              aria-label="Selecionar todas"
                            />
                          </TableHead>
                        )}
                        <TableHead className="text-slate-950">Shipment</TableHead>
                        <TableHead className="text-slate-950">NF</TableHead>
                        <TableHead className="text-slate-950">Cte</TableHead>
                        <TableHead className="text-slate-950">
                          Data da estadia
                        </TableHead>
                        <TableHead className="text-slate-950">
                          Protocolo
                        </TableHead>
                        <TableHead className="text-slate-950">Placa</TableHead>
                        <TableHead className="text-slate-950">
                          Transportadora
                        </TableHead>
                        <TableHead className="text-right text-slate-950">
                          Valor
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billingItems.map((item) => (
                        <TableRow key={item.id}>
                          {canManageBillingItems && (
                            <TableCell className="w-[20px] print:hidden">
                              <Checkbox
                                checked={selected.includes(item.viagemControleId)}
                                onCheckedChange={(checked) =>
                                  toggleItem(
                                    item.viagemControleId,
                                    checked === true
                                  )
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell>{item.viagem?.shipment || '---'}</TableCell>
                          <TableCell>{item.viagem?.notas || '---'}</TableCell>
                          <TableCell>{item.viagem?.conhecimento || '---'}</TableCell>
                          <TableCell>
                            {item.viagem?.agendamento
                              ? Formatter.date(item.viagem.agendamento, {
                                  format: 'dd/MM/yyyy',
                                })
                              : '---'}
                          </TableCell>
                          <TableCell>
                            {item.viagem?.approvalProtocol || '---'}
                          </TableCell>
                          <TableCell>{item.viagem?.placa || '---'}</TableCell>
                          <TableCell>
                            {data?.carrier?.tradeName || '---'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatViagemCurrency(item.viagem?.valorEstadia)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!billingItems.length && (
                        <TableRow>
                          <TableCell
                            colSpan={canManageBillingItems ? 9 : 8}
                            className="text-center"
                          >
                            Sem viagens nesta fatura.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </section>

              <DialogFooter className="print:hidden">
                <div className="mr-auto flex gap-2">
                  <Button.Root
                    size="sm"
                    disabled={
                      !data?.id ||
                      !isPending ||
                      approveMutation.isLoading ||
                      rejectMutation.isLoading
                    }
                    onClick={handleApprove}
                  >
                    <span className="relative grid place-items-center">
                      <span className={approveMutation.isLoading ? 'invisible' : ''}>Aprovar</span>
                      {approveMutation.isLoading && <Loader className="absolute h-4 w-4 animate-spin" />}
                    </span>
                  </Button.Root>
                  <Button.Root
                    size="sm"
                    variant="danger"
                    disabled={
                      !data?.id ||
                      !isPending ||
                      approveMutation.isLoading ||
                      rejectMutation.isLoading
                    }
                    onClick={handleReject}
                  >
                    Reprovar
                  </Button.Root>
                  <Button.Root
                    size="sm"
                    variant="danger"
                    disabled={!canReverseEstadiaBilling || !isPending || approveMutation.isLoading || rejectMutation.isLoading || reverseMutation.isLoading}
                    onClick={handleReverse}
                  >
                    Estornar Fatura
                  </Button.Root>
                </div>
                {canRemoveEstadiaFromBilling && (
                  <Button.Root
                    size="sm"
                    disabled={
                      isApproved || !selected.length || removeMutation.isLoading
                    }
                    variant="danger"
                    onClick={handleRemove}
                  >
                    <span className="relative grid place-items-center">
                      <span
                        className={removeMutation.isLoading ? 'invisible' : ''}
                      >
                        Remover
                      </span>
                      {removeMutation.isLoading && (
                        <Loader className="absolute h-4 w-4 animate-spin" />
                      )}
                    </span>
                  </Button.Root>
                )}
                <Button.Root
                  size="sm"
                  variant="secondary"
                  onClick={closeDialog}
                >
                  Cancelar
                </Button.Root>
                <Button.Root size="sm" onClick={() => window.print()}>
                  Imprimir
                </Button.Root>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        ref={removeConfirmationDialogRef}
        confirmationButtonLabel="Remover"
        cancelButtonLabel="Cancelar"
      />
      <ConfirmationDialog
        ref={approveConfirmationDialogRef}
        confirmationButtonLabel="Aprovar"
        cancelButtonLabel="Cancelar"
      />
      <ViagensBillingRejectDialog
        billingId={rejectingBillingId}
        loading={rejectMutation.isLoading}
        onOpenChange={(nextOpen) => !nextOpen && setRejectingBillingId(null)}
        onSubmit={(payload) => data?.id && rejectMutation.mutate({ id: data.id, ...payload })}
      />
      <ViagensBillingEstornoDialog
        billingId={reversingBillingId}
        loading={reverseMutation.isLoading}
        onOpenChange={(nextOpen) => !nextOpen && setReversingBillingId(null)}
        onSubmit={(payload) => data?.id && reverseMutation.mutate({ id: data.id, ...payload })}
      />
    </>
  );
};
